import React from "react";
import Plot from "react-plotly.js";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import "./PlotlyGraph.scss";
import { Divider } from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Graph from "../Model/Graph";
import {
  ChartArea,
  Lightbulb,
  Link,
  PianoIcon,
  PiIcon,
  PinIcon,
} from "lucide-react";
import { FaPinterest } from "react-icons/fa";

const PlotlyGraph = ({ plotData, rearrangeReferences, data }) => {
  const exportToPDF = (id, title) => {
    const element = document.getElementById(id);

    // Hide controls by adding the no-export class
    const controls = element.querySelectorAll(".export-btn, .expand-btn");
    controls.forEach((btn) => btn.classList.add("no-export"));

    html2canvas(element, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const imgWidth = 180;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const marginTop = 20;
      const marginLeft = (pageWidth - imgWidth) / 2;

      pdf.addImage(imgData, "PNG", marginLeft, marginTop, imgWidth, imgHeight);
      pdf.save(`${title || "chart"}.pdf`);

      // Restore buttons
      controls.forEach((btn) => btn.classList.remove("no-export"));
    });
  };

  if (!plotData || !Array.isArray(plotData) || plotData.length === 0) {
    return null;
  }

  console.log("PlotlyGraph message", plotData);

  console.log("data message", data);


  return (
    <div
      style={{ gap: "1rem" }}
      // className="grid grid-cols-1 bg-gray-50 border p-10 rounded-xl"
      id="chatAreaNokia"
    >
      {plotData.map((plot, idx) => {
        const chartId = `plot-container-${idx}`;

        const chartData =
          plot.data && plot.data[0]?.type === "indicator"
            ? [
                {
                  type: "indicator",
                  mode: "number+gauge",
                  value: plot.data[0].y ? plot.data[0].y[0] ?? 0 : 0,
                  title: { text: plot.data[0].name || "" },
                  gauge: {
                    // axis: { range: [0, 100] },
                    axis: {
                      range: [
                        Math.min(0, plot.data[0].y[0] - 10), // extend below if negative
                        Math.max(100, plot.data[0].y[0] + 10), // extend above value if >100
                      ],
                    },
                    bar: { color: "#005aff" },
                  },
                },
              ]
            : plot.data;

        return (
          <React.Fragment key={idx}>
            <div
              key={idx}
              id={chartId}
              style={{ borderLeft: "5px solid #005aff", padding: "30px" }}
              className="plot-card bg-white rounded-xl"
            >
              <div className="mb-4">
                <div
                  style={{
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                  className="mb-3 flex"
                >
                  <span
                    // style={{ fontSize: "24px", borderLeft:"3px solid #005aff" }}
                    // style={{ fontSize: "24px", borderLeft:"3px solid #005aff" }}

                    className="text-[28px] font-semibold text-gray-800 grid gap-2"
                    // className="text-lg font-semibold text-gray-800 px-2 p-2 bg-gray-50 rounded"
                  >
                    <span>{plot.title}</span>
                    <span className="text-[15px] text-[#7f8c8d] italic">
                      {plot.description}
                    </span>
                  </span>
                  <button
                    onClick={() => exportToPDF(chartId, plot.title)}
                    style={{ border: "none" }}
                    className="bg-[#005aff] p-2 text-white rounded"
                  >
                    Export in PDF
                  </button>
                </div>
              </div>

              <div className="grid flex-wrap gap-6">
                {/* Chart Section */}

                {/* Markdown Text Section */}
                <div
                  style={{ fontSize: "20px" }}
                  className="text-gray-600 leading-relaxed grid"
                >
                  <span
                    // style={{ fontSize: "20px" }}
                    style={{
                      fontSize: "18px",
                      borderLeft: "3px solid #005aff",
                    }}
                    className="text-lg font-semibold text-gray-800 mb-2 block px-2 py-2 rounded bg-gradient-to-r from-[#f39c12]/20 to-[#005aff]/70 mb-4"
                  >
                    Original Complete Context
                  </span>

                  <span className="bg-[#f8f9fa] p-6 border rounded">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        a: ({ node, ...props }) => (
                          <a
                            {...props}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline text-blue-600"

                          />
                        ),
                      }}
                    >
                      {rearrangeReferences(
                        plot.original_complete_context || "No Data available"
                      )}
                    </ReactMarkdown>
                  </span>
                </div>

                <span
                  // style={{ fontSize: "20px" }}
                  style={{ fontSize: "18px", borderLeft: "3px solid #005aff" }}
                  className="text-lg font-semibold text-gray-800 mb-2 block px-2 py-2 rounded bg-gradient-to-r from-[#f39c12]/20 to-[#005aff]/70 mb-2"
                >
                  Data Visualization
                </span>
                <div className="rounded-lg p-2 border bg-white relative flex-1 min-w-[350px shadow">
                  <Graph chartData={chartData} plot={plot} />

                  <Plot
                    data={chartData}
                    layout={{
                      ...plot.layout,
                      autosize: true,
                      responsive: true,
                      showlegend: true,
                      legend: {
                        orientation: "h",
                        x: 0.5,
                        xanchor: "center",
                        y: -0.7,
                        yanchor: "top",
                      },
                      plot_bgcolor: "rgba(0,0,0,0)",
                      paper_bgcolor: "rgba(0,0,0,0)",
                      font: { family: "Arial, sans-serif" },
                      title: {
                        font: { size: 16, family: "Arial, sans-serif" },
                      },
                      colorway: [
                        "#005aff",
                        "#009966",
                        "#0000a0",
                        "#6d32a8",
                        "#bb1ccc",
                        "#cc0000",
                      ],
                      xaxis: {
                        title: {
                          text: plot.layout?.xaxis?.title || "X Axis",
                          font: { size: 12, family: "Arial, sans-serif" },
                        },
                        tickangle: -45,
                        tickfont: { size: 10 },
                        automargin: true,
                      },
                      yaxis: {
                        title: {
                          text: plot.layout?.yaxis?.title || "Y Axis",
                          font: { size: 12, family: "Arial, sans-serif" },
                        },
                        tickfont: { size: 10 },
                      },
                    }}
                    useResizeHandler
                    style={{ width: "100%", minHeight: "450px" }}
                    config={{
                      displaylogo: false,
                      modeBarButtonsToRemove: [
                        "lasso2d",
                        "select2d",
                        "hoverClosestCartesian",
                        "hoverCompareCartesian",
                        "toggleSpikelines",
                      ],
                      responsive: true,
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Divider with red bottom border, except after the last plot */}
            {idx < plotData.length - 1 && (
              <Divider
                sx={{
                  // borderBottom: "2px solid red",
                  margin: "40px 0", // optional spacing
                  background:
                    "linear-gradient(90deg, #3498db, #e74c3c, #27ae60, #f39c12)",
                  height: "2px",
                  borderRadius: "2px",
                }}
              />
            )}
          </React.Fragment>
        );
      })}

      <Divider
        sx={{
          // borderBottom: "2px solid red",
          margin: "40px 0", // optional spacing
          background:
            "linear-gradient(90deg, #3498db, #e74c3c, #27ae60, #f39c12)",
          height: "2px",
          borderRadius: "2px",
        }}
      />

      <div
        style={{
          borderLeft: "5px solid #f39c12",
          borderTop: "3px solid #f39c12",
        }}
        className="bg-white rounded-xl mt-7 p-4 [box-shadow:0_8px_25px_rgba(0,0,0,0.1)]"
      >
        <span className="bg-white rounded-xl">
          <span
            // style={{ fontSize: "20px" }}
            style={{ fontSize: "18px", borderLeft: "3px solid #005aff" }}
            className="text-lg font-semibold text-gray-800 mb-2 block px-2 py-2 rounded bg-gradient-to-r from-[#f39c12]/20 to-[#005aff]/70 mb-4"
          >
            <Link width={15} /> Additional Information
          </span>

          <span className="bg-[#ffeaa7]/50 p-6 block border border-[#ffeaa7] rounded">
            <span className="mb-3 block">
              <Lightbulb className="text-[#005aff]" />
            </span>
            {/* <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeRaw]}
            >
              {rearrangeReferences(data || "No Data available")}
            </ReactMarkdown> */}

            <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      rehypePlugins={[rehypeRaw]}
                      components={{
                        a: ({ node, ...props }) => (
                          <a
                            {...props}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline text-blue-600"

                          />
                        ),
                      }}
                    >
                      {rearrangeReferences(
                        data || "No Data available"
                      )}
                    </ReactMarkdown>
          </span>
        </span>
      </div>
    </div>
  );
};

export default PlotlyGraph;
